import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';

export interface Language {
  code: string;
  name: string;
  is_default?: boolean;
  // Flag emoji supplied by the server (may be null until the backend ships it).
  flag_value?: string;
  // The language's own name, parsed from `name` ("<English> - <native>") and title-cased
  // in its own locale. Rendered as the row label in the language sheet.
  nativeName?: string;
}

interface ServerLanguage {
  id?: number;
  code: string;
  name?: string;
  is_default?: boolean;
  flag_value?: string;
}

const FALLBACK_LANG = 'en';

// Per-DEVICE explicit language choice. Written ONLY when the diner taps a row in the
// language sheet (via chooseLanguage), never by a silently-applied venue default. Lives in
// localStorage so it survives venue switches; preserved across the splash localStorage.clear().
const DEVICE_PREF_KEY = 'devicePreferredLanguage';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  // App-bundled UI localizations (must match files in /assets/i18n)
  private readonly bundledLanguageCodes = new Set<string>([
    'en', 'mk', 'sq', 'sv', 'sr', 'tr', 'bg', 'ro', 'hr',
    'de', 'fr', 'nl', 'el', 'pl', 'it'
  ]);

  // Legacy codes that older sessions may still have persisted, mapped onto the
  // code the server actually sends. Serbian shipped as 'rs' here while the
  // backend `languages` table has only ever had 'sr', so Serbian venues fell
  // through to the English UI.
  private readonly legacyCodeAliases: Record<string, string> = {
    rs: 'sr'
  };

  // Display names for the case where the server hasn't supplied any languages yet
  private readonly bundledFallbackLanguages: Language[] = [
    { code: 'en', name: 'English' },
    { code: 'mk', name: 'Macedonian' },
    { code: 'sq', name: 'Albanian' },
    { code: 'sv', name: 'Swedish' },
    { code: 'sr', name: 'Serbian' },
    { code: 'tr', name: 'Turkish' },
    { code: 'bg', name: 'Bulgarian' },
    { code: 'ro', name: 'Romanian' },
    { code: 'hr', name: 'Croatian' },
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
    { code: 'nl', name: 'Dutch' },
    { code: 'el', name: 'Greek' },
    { code: 'pl', name: 'Polish' },
    { code: 'it', name: 'Italian' }
  ];

  // Content language — reflects user selection; drives meta_data lookups.
  private currentLangSubject = new BehaviorSubject<string>(FALLBACK_LANG);
  public currentLang$ = this.currentLangSubject.asObservable();

  // Emits ONLY when the diner explicitly picks a language in the sheet (via chooseLanguage) —
  // never on a silent/default/background apply. Used to time the post-language push prompt.
  private languageChosenSubject = new Subject<string>();
  public languageChosen$ = this.languageChosenSubject.asObservable();

  // UI language — bundled code or FALLBACK_LANG; drives ngx-translate.
  public uiLang$: Observable<string> = this.currentLang$.pipe(
    map(code => this.resolveUiLang(code)),
    distinctUntilChanged()
  );

  // Venue languages straight from the server — not filtered by bundled set.
  private venueLanguagesSubject = new BehaviorSubject<Language[]>([]);
  public venueLanguages$ = this.venueLanguagesSubject.asObservable();

  // Shops for which the first-visit sheet has already been offered this page-load.
  // In-memory (mirrors Android's `firstVisitOffered`): survives component re-init but
  // resets on a real reload / re-scan, so the sheet re-offers on a genuine next visit.
  private offeredShops = new Set<string>();

  // Loaded translation bundles keyed by locale, so the sheet can resolve the title /
  // "Suggested" / "Current" tag in a language other than the active UI one without refetching.
  private localeStringsCache = new Map<string, any>();

  constructor(
    private translate: TranslateService,
    // The configured TranslateHttpLoader. Injected directly so a locale bundle can be fetched
    // without going through TranslateService — see getLocaleTranslations().
    private translateLoader: TranslateLoader
  ) {
    this.translate.use(FALLBACK_LANG);
  }

  getCurrentLang(): string {
    return this.currentLangSubject.value;
  }

  getVenueLanguages(): Language[] {
    return this.venueLanguagesSubject.value;
  }

  // Used when there is no clientData yet.
  getBundledLanguages(): Language[] {
    return this.bundledFallbackLanguages;
  }

  isBundled(code: string | null | undefined): boolean {
    return !!code && this.bundledLanguageCodes.has(this.normalizeCode(code));
  }

  // Lower-cases and maps any legacy alias onto the server's code, so a value
  // persisted by an older build still resolves.
  private normalizeCode(code: string | null | undefined): string {
    const lower = (code || '').toLowerCase();
    return this.legacyCodeAliases[lower] ?? lower;
  }

  // Apply-only: switch the UI (ngx-translate) + content language and re-render. Does NOT
  // persist anything — the durable device preference is written separately by chooseLanguage.
  setLanguage(langCode: string): void {
    const normalized = this.normalizeCode(langCode);
    if (!normalized) return;

    const uiLang = this.resolveUiLang(normalized);
    this.translate.use(uiLang);
    this.currentLangSubject.next(normalized);
  }

  // The diner explicitly picked this language in the sheet: remember it per-device
  // (survives venue switches) and apply it immediately.
  chooseLanguage(langCode: string): void {
    const normalized = this.normalizeCode(langCode);
    if (!normalized) return;
    localStorage.setItem(DEVICE_PREF_KEY, normalized);
    this.setLanguage(normalized);
    this.languageChosenSubject.next(normalized);
  }

  // Silently applies the right language for a venue and reports whether the caller
  // should offer the first-visit sheet. Serve the diner's explicit device choice when the
  // venue offers it; otherwise apply a sane venue default and (if multilingual) prompt.
  initializeForClient(
    shopId: number | string,
    serverLanguages: ServerLanguage[] | null | undefined
  ): { needsSheet: boolean } {
    const venue = this.mapVenueLanguages(serverLanguages);
    this.venueLanguagesSubject.next(venue);

    const codes = venue.map(v => v.code);
    const pref = localStorage.getItem(DEVICE_PREF_KEY);

    if (pref && codes.includes(this.normalizeCode(pref))) {
      this.setLanguage(pref);
      return { needsSheet: false };
    }

    this.setLanguage(this.resolveVenueDefault(venue, codes));
    return { needsSheet: codes.length > 1 };
  }

  // is_default → en (if served) → first. Empty venue → en; single-language → that one.
  private resolveVenueDefault(venue: Language[], codes: string[]): string {
    if (codes.length === 0) return FALLBACK_LANG;
    if (codes.length === 1) return codes[0];
    const defaultLang = venue.find(v => v.is_default);
    if (defaultLang) return defaultLang.code;
    if (codes.includes(FALLBACK_LANG)) return FALLBACK_LANG;
    return codes[0];
  }

  // The language to pin as "Suggested" on first visit: the device/browser locale IF the
  // venue serves it AND it is not English (English is never suggested — many phones default
  // to it, so endorsing it would push locals into an English menu). Distinct from the stored
  // device preference: this reflects the browser, not a past choice.
  getSuggestedLanguageCode(): string | null {
    const raw = (typeof navigator !== 'undefined' ? navigator.language : '') || '';
    const device = this.normalizeCode(raw.split('-')[0]);
    if (!device || device === FALLBACK_LANG) return null;
    const match = this.venueLanguagesSubject.value.find(v => v.code === device);
    return match ? match.code : null;
  }

  hasOffered(shopId: number | string): boolean {
    return this.offeredShops.has(String(shopId));
  }

  markOffered(shopId: number | string): void {
    this.offeredShops.add(String(shopId));
  }

  // Load a locale's translation bundle (for the sheet title / tag, which render in a language
  // other than the active UI one) without switching the active language. Non-bundled codes fall
  // back to English. Cached so repeated opens don't refetch.
  //
  // Reads through the loader rather than TranslateService: ngx-translate 17 removed
  // `TranslateService.getTranslation()`, and the loader is what that method called internally
  // anyway. The one behavioural difference is that the result is no longer merged into the
  // translation store — nothing depends on that, since the only consumer (the language sheet)
  // reads the returned bundle directly. It also means this can no longer perturb the active
  // language, which the old path could.
  getLocaleTranslations(locale: string | null | undefined): Promise<any> {
    const code = this.isBundled(locale) ? this.normalizeCode(locale) : FALLBACK_LANG;
    const cached = this.localeStringsCache.get(code);
    if (cached) return Promise.resolve(cached);
    return firstValueFrom(this.translateLoader.getTranslation(code))
      .then(res => {
        this.localeStringsCache.set(code, res);
        return res;
      })
      .catch(() => this.localeStringsCache.get(FALLBACK_LANG) ?? {});
  }

  shouldShowSwitcher(): boolean {
    return this.venueLanguagesSubject.value.length >= 2;
  }

  // --- Content localization helpers (English fallback built in) ---

  // Look up a product's meta_data entry by key, falling back to the English
  // entry then to the provided default. Handles both "<lang>" and "<lang>_<suffix>" keys.
  localizeMetaValue(
    metaData: Array<{ key: string; value: string }> | null | undefined,
    suffix: string,
    defaultValue: string
  ): string {
    if (!metaData || metaData.length === 0) return defaultValue;
    const target = this.currentLangSubject.value;
    const primary = metaData.find(m => m.key === this.metaKey(target, suffix));
    if (primary?.value) return primary.value;
    const english = metaData.find(m => m.key === this.metaKey(FALLBACK_LANG, suffix));
    if (english?.value) return english.value;
    return defaultValue;
  }

  // Parse a JSON blob of {langCode: name} and pick the current lang with English fallback.
  localizeJsonMap(jsonSource: string | null | undefined, defaultValue: string): string {
    if (!jsonSource) return defaultValue;
    let parsed: Record<string, string> | null = null;
    try {
      parsed = JSON.parse(jsonSource);
    } catch {
      return defaultValue;
    }
    if (!parsed || typeof parsed !== 'object') return defaultValue;

    const target = this.currentLangSubject.value;
    if (parsed[target]) return parsed[target];
    if (parsed[FALLBACK_LANG]) return parsed[FALLBACK_LANG];
    return defaultValue;
  }

  // Pick the current language from an already-parsed {langCode: text} map with an
  // English fallback, then the provided default (the scalar the dashboard mirrors
  // for older builds). Same semantics as localizeJsonMap but for native objects —
  // the marketing Firestore docs (promotions, events, notifications) store real maps.
  localizeMap(map: Record<string, string> | null | undefined, defaultValue: string): string {
    if (!map || typeof map !== 'object') return defaultValue;
    const target = this.currentLangSubject.value;
    if (map[target]) return map[target];
    if (map[FALLBACK_LANG]) return map[FALLBACK_LANG];
    return defaultValue;
  }

  private metaKey(lang: string, suffix: string): string {
    return suffix ? `${lang}_${suffix}` : lang;
  }

  private mapVenueLanguages(serverLanguages: ServerLanguage[] | null | undefined): Language[] {
    const seen = new Map<string, Language>();
    for (const raw of serverLanguages || []) {
      const code = this.normalizeCode(raw?.code);
      if (!code) continue;
      const name = raw.name || this.fallbackName(code);
      const entry: Language = {
        code,
        name,
        is_default: !!raw.is_default,
        flag_value: raw.flag_value,
        nativeName: this.parseNativeName(name, code)
      };
      const existing = seen.get(code);
      if (!existing) {
        seen.set(code, entry);
      } else if (entry.is_default && !existing.is_default) {
        seen.set(code, entry);
      }
    }
    return Array.from(seen.values());
  }

  // Server `name` is "<English name> - <native name>"; take the native half (fall back to the
  // whole string when the separator is absent) and title-case the first character in the
  // language's own locale (matters for Turkish dotted-İ).
  private parseNativeName(name: string, code: string): string {
    const raw = (name || '').trim();
    if (!raw) return code.toUpperCase();
    const sep = ' - ';
    const native = raw.includes(sep) ? raw.slice(raw.indexOf(sep) + sep.length).trim() : raw;
    const base = native || raw;
    if (!base) return code.toUpperCase();
    return base.charAt(0).toLocaleUpperCase(code) + base.slice(1);
  }

  private fallbackName(code: string): string {
    const bundled = this.bundledFallbackLanguages.find(l => l.code === code);
    return bundled ? bundled.name : code;
  }

  private resolveUiLang(code: string): string {
    const normalized = this.normalizeCode(code);
    return this.isBundled(normalized) ? normalized : FALLBACK_LANG;
  }
}
